import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  Button,
  LinearProgress,
  FormHelperText,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { TrashIcon, UploadDocumentIcon } from "../../icons";
import { fileUpload, fileDelete } from "../../api/uploadApi";
import ValidationErrorText from "../common/ValidationErrorText";

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 10 }, (_, i) => currentYear - i);

const toNumberOrEmpty = (v) =>
  v === "" || v === null || v === undefined ? "" : Number(v);

const defaultAward = {
  year: currentYear,
  body: "",
  title: "",
  fileUrl: "",
  uploading: false,
  progress: 0,
  uploadError: "",
};

const AwardsForm = ({
  label,
  value = [],
  onChange,
  readOnly = false,
  fieldType,
  fieldErrors,
}) => {
  // Local copy, always cloned
  const [awards, setAwards] = useState([]);

  // Sync with parent if updated externally
  useEffect(() => {
    if (value && Array.isArray(value)) {
      setAwards(value.map((a) => ({
              ...defaultAward,
              ...a,
              year: toNumberOrEmpty(a?.year ?? defaultAward.year),
            }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(()=>{
    if(value?.length==0){
      let list = Array(5).fill().map(() => ({ ...defaultAward }))
      setAwards(list)
      onChange(list)
    }
  },[])

  const updateAwards = (arr) => {
    setAwards(arr);
    onChange && onChange(arr);
  };

  const handleChange = (index, key) => (e) => {
    let val = e.target.value;
    if (key === "year") {
      val = val === "" ? "" : Number(val); // keep numeric in state
    }
    const arr = awards.map((item, i) =>
      i === index ? { ...item, [key]: val } : item
    );
    updateAwards(arr);
  };

  const handleAddMore = () => {
    updateAwards([...awards, { ...defaultAward }]);
  };

  const handleRemove = (index) => {
    if (awards.length <= 0) return;
    updateAwards(awards.filter((_, i) => i !== index));
  };

  // --- Purely immutable upload ---
  const handleFileChange = (index) => async (e) => {
    const file = e.target.files[0];
    if (!file) return;

        // Allowed MIME types
  const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/jpg", "application/pdf"];

    // Validate file type
  if (!allowedTypes.includes(file?.type)) {
      alert("Only image files (jpg, jpeg, png, webp) or PDF files are allowed.");
      return;
    }

    // Set uploading state
    let arr = awards.map((item, i) =>
      i === index
        ? { ...item, uploading: true, uploadError: "", progress: 0 }
        : item
    );
    setAwards(arr);

    try {
      const data = await fileUpload(file, (prog) => {
        // Progress callback is always safe and immutable
        arr = arr.map((item, i) =>
          i === index ? { ...item, progress: prog } : item
        );
        setAwards(arr);
      });
      arr = arr.map((item, i) =>
        i === index
          ? {
              ...item,
              fileUrl: data.url || "",
              uploading: false,
              progress: 100,
              uploadError: "",
            }
          : item
      );
    } catch (err) {
      arr = arr.map((item, i) =>
        i === index
          ? {
              ...item,
              uploading: false,
              progress: 0,
              uploadError: "Upload failed!",
            }
          : item
      );
    }
    updateAwards(arr);
  };

  const handleRemoveFile = (index) => async () => {
    let arr = [...awards];
    const fileUrl = arr[index]?.fileUrl;
    if (fileUrl) {
      try {
        await fileDelete(fileUrl);
      } catch {
        arr = arr.map((item, i) =>
          i === index
            ? { ...item, uploadError: "Delete from server failed!" }
            : item
        );
        updateAwards(arr);
        return;
      }
    }
    arr = arr.map((item, i) =>
      i === index
        ? {
            ...item,
            fileUrl: "",
            progress: 0,
            uploading: false,
            uploadError: "",
          }
        : item
    );
    updateAwards(arr);
  };

  return (
    <Box>
      <Typography fontSize="14px" fontWeight="500" mb={2}>
        {label}
      </Typography>

      {awards.map((award, index) => (
        <Box
          key={index}
          sx={{
            mb: 3,
            border: "none",
            borderRadius: 2,
            p: 0,
            position: "relative",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <Typography fontSize="13px" fontWeight={500} mb={0.5}>
                Year
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={award.year === "" ? "" : Number(award.year)}
                onChange={handleChange(index, "year")}
                disabled={readOnly}
              >
                {/* If you want a placeholder, uncomment:
                <MenuItem value="" disabled>Select Year</MenuItem> */}
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
              <ValidationErrorText error={fieldErrors?.[`${fieldType}[${index}].year`]}/>
            </Grid>

            <Grid item xs={12} md={5}>
              <Typography fontSize="13px" fontWeight={500} mb={0.5}>
                Awarding Body
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={award.body}
                onChange={handleChange(index, "body")}
                disabled={readOnly}
              />
              <ValidationErrorText error={fieldErrors?.[`${fieldType}[${index}].body`]}/>
            </Grid>

            <Grid item xs={12} md={5}>
              <Typography fontSize="13px" fontWeight={500} mb={0.5}>
                Award / Ranking Title
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={award.title}
                  onChange={handleChange(index, "title")}
                  disabled={readOnly}
                />
                {awards.length > 0 && !readOnly && (
                  <IconButton onClick={() => handleRemove(index)} color="error">
                    <TrashIcon />
                  </IconButton>
                )}
              </Box>
                <ValidationErrorText error={fieldErrors?.[`${fieldType}[${index}].title`]}/>
            </Grid>

            <Grid item xs={12}>
              <Typography fontSize="13px" fontWeight={500} mb={0.5}>
                Certificate Upload
              </Typography>

              {award.fileUrl ? (
                <Box
                  sx={{
                    border: "1px solid #ccc",
                    padding: 2,
                    maxWidth: "300px",
                    borderRadius: 2,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <UploadDocumentIcon color="action" />
                    <Typography fontSize="14px" sx={{ width: "100%" }}>
                      <a
                        href={award.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View Uploaded
                      </a>
                    </Typography>
                    {!readOnly && (
                      <IconButton size="small" onClick={handleRemoveFile(index)}>
                        <TrashIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  {award.uploading && (
                    <Box sx={{ width: "100%", mt: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={award.progress}
                        sx={{ height: 6, borderRadius: 5, width: "100px" }}
                      />
                      <Typography fontSize={12} color="primary">
                        Uploading...
                      </Typography>
                    </Box>
                  )}
                  {award.uploadError && (
                    <Typography color="error" fontSize={12} mt={1}>
                      {award.uploadError}
                    </Typography>
                  )}
                </Box>
              ) : (
                !readOnly && (
                  <Box>
                    <Button
                      component="label"
                      sx={{
                        color: "#1075FF",
                        border: "1px solid #1075FF",
                        textTransform: "capitalize",
                        px: 3,
                      }}
                      startIcon={<CloudUploadIcon />}
                      size="small"
                      disabled={award.uploading}
                    >
                      {award.uploading ? "Uploading..." : "Upload Certificate"}
                      <input
                        type="file"
                        hidden
                        accept=".pdf,.png"
                        onChange={handleFileChange(index)}
                        disabled={award.uploading}
                      />
                    </Button>
                    <Typography fontSize="12px" color="gray" mt={0.5}>
                      Upload a clear PDF / PNG, Max 2MB
                    </Typography>
                    {award.uploadError && (
                      <Typography color="error" fontSize={12} mt={1}>
                        {award.uploadError}
                      </Typography>
                    )}
                <ValidationErrorText error={fieldErrors?.[`${fieldType}[${index}].fileUrl`]}/>
                  </Box>
                )
              )}
            </Grid>
          </Grid>
        </Box>
      ))}

      {!readOnly && (
        <Button
          sx={{
            color: "#1075FF",
            border: "1px solid #1075FF",
            textTransform: "capitalize",
            px: 3,
          }}
          onClick={handleAddMore}
        >
          + Add More
        </Button>
      )}
    </Box>
  );
};

export default AwardsForm;
